import React, { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { TrackingClient } from '@sparefoot/tracking-client';
import './YoutubeVideo.scss';

const YoutubeVideo = ({ src, videoName }) => {
	const [videoDuration, setVideoDuration] = useState(0);
	const [playedTime, setPlayedTime] = useState(0);
	const [hasSentEvent, setHasSentEvent] = useState(false);
	const [player, setPlayer] = useState(null);
	const [intervalId, setIntervalId] = useState(null);
	const iframeRef = useRef(null);

	// Use refs to avoid stale closures in YouTube player callbacks
	const playerRef = useRef(null);
	const intervalRef = useRef(null);
	const hasSentEventRef = useRef(false);
	const videoDurationRef = useRef(0);
	const playedTimeRef = useRef(0);

	playerRef.current = player;
	intervalRef.current = intervalId;
	hasSentEventRef.current = hasSentEvent;
	videoDurationRef.current = videoDuration;
	playedTimeRef.current = playedTime;

	const trackVideoEvent = useCallback(() => {
		setHasSentEvent(true);
		TrackingClient.trackVideoWatched({
			video_duration: videoDurationRef.current,
			video_duration_played: playedTimeRef.current,
			video_name: videoName,
		});
	}, [videoName]);

	const onStateChange = useCallback((event) => {
		// This method is called anytime the video state changes
		// playing, paused, ended, buffering, unstarted
		const playerState = window.YT.PlayerState;
		if (event.data === playerState.PLAYING && !intervalRef.current) {
			const newInterval = setInterval(() => {
				if (playerRef.current) {
					setPlayedTime(playerRef.current.getCurrentTime());
				}
			}, 1000);
			setIntervalId(newInterval);
		} else if (playerRef.current?.getCurrentTime() > 10 && !hasSentEventRef.current) {
			// If the video has played more than 10 seconds, and we haven't sent a tracking evnet yet
			// Keep in mind this only fires on state changes, so when the video ends, pauses, etc
			trackVideoEvent();
			clearInterval(intervalRef.current);
			setIntervalId(null);
		} else {
			clearInterval(intervalRef.current);
			setIntervalId(null);
		}
	}, [trackVideoEvent]);

	useEffect(() => {
		const onYouTubeIframeAPIReady = () => {
			// If the iframe doesn't exist, or the player isn't created return
			if (!iframeRef.current || !window.YT) return;

			// Binds iframeRef to the iframe and manages player control assignment
			iframeRef.current.id = `youtube-player-${Math.random()}`;
			const newPlayer = new window.YT.Player(iframeRef.current.id, {
				events: {
					onReady: (event) => {
						setVideoDuration(event.target.getDuration()); // When the video loads, set the total video duration for tracking event
					},
					onStateChange, // Use the explicitly bound function
				},
			});
			setPlayer(newPlayer);
		};

		// Create a script tag to use the Youtube Iframe Player API/make the iframe a controlled component
		if (!window.YT) {
			const tag = document.createElement('script');
			tag.src = 'https://www.youtube.com/iframe_api';
			document.body.appendChild(tag);
			window.onYouTubeIframeAPIReady = onYouTubeIframeAPIReady;
		} else {
			onYouTubeIframeAPIReady();
		}

		// Verifies interval stops firing on navigation
		return () => {
			clearInterval(intervalRef.current);
		};
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<div className="ph-responsive-embed">
			<iframe
				title={videoName || 'video'}
				src={src}
				ref={iframeRef}
				allowFullScreen
				referrerPolicy="strict-origin-when-cross-origin"
			/>
		</div>
	);
};

YoutubeVideo.propTypes = {
	src: PropTypes.string.isRequired,
	videoName: PropTypes.string.isRequired,
};

export default YoutubeVideo;
